import { api } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'
import { getUserLocale } from '@/lib/locale'
import type {
  BalanceSheetResponse,
  MappingPatch,
  TbAutoMapResponse,
  TbDiagnosticsResponse,
  TbImportResponse,
  TbIncludeSkippedRequest,
  TbIncludeSkippedResponse,
  TbMappingsResponse,
  TbUploadResponse,
  TbValidationResponse,
} from './types'

const BASE = '/api/reporting'

/**
 * Multipart upload needs to bypass the JSON transport - we call fetch
 * directly but still rely on tokenManager for the Bearer header.
 */
async function uploadTrialBalance(file: File, asOfDate: string): Promise<TbUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('asOfDate', asOfDate)
  const token = tokenManager.get()
  const resp = await fetch(`${BASE}/tb-import/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
    credentials: 'same-origin',
  })
  const contentType = resp.headers.get('content-type') || ''
  const body = contentType.includes('application/json') ? await resp.json() : null
  if (!resp.ok) {
    const msg = (body && (body as { error?: string }).error) || `Upload failed (${resp.status}).`
    throw new Error(msg)
  }
  return body as TbUploadResponse
}

export const tbImportApi = {
  upload: uploadTrialBalance,
  getMappings: (batchId: string) =>
    api.get<TbMappingsResponse>(`${BASE}/tb-import/${batchId}/mappings`),
  autoMap: (batchId: string) =>
    api.post<TbAutoMapResponse>(`${BASE}/tb-import/${batchId}/auto-map`),
  saveMappings: (batchId: string, rows: MappingPatch[]) =>
    api.put<{ message?: string }>(`${BASE}/tb-import/${batchId}/mappings`, { rows }),
  validate: (batchId: string) =>
    api.post<TbValidationResponse>(`${BASE}/tb-import/${batchId}/validate`),
  runImport: (
    batchId: string,
    opts: { fiscalStartMonth?: number; confirmDuplicate?: boolean } = {},
  ) =>
    api.post<TbImportResponse>(`${BASE}/tb-import/${batchId}/import`, {
      fiscalStartMonth: opts.fiscalStartMonth ?? getUserLocale().fiscalStartMonth,
      confirmDuplicate: opts.confirmDuplicate ?? false,
    }),
  includeSkipped: (batchId: string, rows: TbIncludeSkippedRequest[]) =>
    api.post<TbIncludeSkippedResponse>(`${BASE}/tb-import/${batchId}/include-skipped`, { rows }),
  diagnostics: (batchId: string) =>
    api.get<TbDiagnosticsResponse>(`${BASE}/tb-import/${batchId}/diagnostics`),
  balanceSheet: (asOfDate: string, fiscalStartMonth?: number) =>
    api.post<BalanceSheetResponse>(`${BASE}/balance-sheet`, {
      asOfDates: [asOfDate],
      fiscalStartMonth: fiscalStartMonth ?? getUserLocale().fiscalStartMonth,
    }),
}
